import { PALLET_STATUSES, RECEIVED_STATUSES, STAGED_STATUSES } from '@/constants/manifestStatuses';
import { collectManifestPalletsFromOrders, planBulkReceive } from '@/utilities/manifestPallets';
import { DOCK_LOCATION } from './constants';
import {
    consolidatableItems,
    hasOpenException,
    getOrderPallets,
    isReceivable,
    orderReceivedPallets,
    orderStagedPallets,
    partitionPlaceholders,
    placeholdersSkippedMessage,
    PLACEHOLDER_PRINT_BLOCKED,
} from './utils';

const uniquePallets = getOrderPallets;

// One child row per item, but the same item can arrive from several selection paths (row + bulk).
// Collapse to unique items before acting on them.
const uniqueItems = (items = []) => [...new Map((items || []).map((item) => [item.item_id, item])).values()];

// The manifest with its orders/items reduced to `items` — what the label builders consume. Null when
// the selection is empty. `items[]` is narrowed too since the print grouping keys off it.
const narrowManifestToItems = (manifest, items = []) => {
    const ids = new Set(uniqueItems(items).map((item) => item.item_id));
    if (!ids.size) return null;
    return {
        ...manifest,
        items: (manifest.items || []).filter((mapping) => ids.has(mapping.item_id)),
        orders: (manifest.orders || [])
            .map((order) => ({
                ...order,
                manifestItems: (order.manifestItems || []).filter((item) => ids.has(item.item_id)),
            }))
            .filter((order) => order.manifestItems.length),
    };
};

export const receivableItems = (items) => items.filter(isReceivable);

// Builds the row/bulk action handlers from the manifest context callbacks. Single-target and
// already-array-capable actions wire straight to the existing hooks (which write order_wh_events);
// the optimized batched bulk-receive is chunk 4.
export const buildGridActions = ({ manifest, callbacks }) => ({
    // Planned up front: this forEach is synchronous, its mutations are not, so a per-item gate would
    // see no sibling as received and two items finishing one order would each defer to the other.
    receive: (items) => {
        const batch = receivableItems(uniqueItems(items));
        // Must be the unfiltered manifest — receivePallet resolves co-tenant orders off that same list.
        const plan = planBulkReceive(manifest?.orders, batch);

        batch.forEach((item) => {
            const completesOrderIds = plan.get(item.item_id) || [];
            if (item?.pallet?.warehouse_status === PALLET_STATUSES.NOT_RECEIVED) {
                callbacks.receivePallet(item, item.pallet, manifest, { completesOrderIds });
            } else {
                callbacks.receiveItem(item, manifest, { completesOrderIds });
            }
        });
    },
    // Assign a location per item — the store ensures each item has its own pallet first, so items
    // without a pallet (common on outbound) become individually locatable rather than doing nothing.
    assignLocation: (items) => {
        // Mirror the row rule for bulk callers: only already-received items get a location. Receiving is
        // what writes the OMS wh_events, and assignItemLocations creates a RECEIVED pallet for palletless
        // items — so letting a NOT_RECEIVED item through here would receive it silently.
        const unique = uniqueItems(items).filter((item) => RECEIVED_STATUSES.includes(item?.pallet?.warehouse_status));
        if (unique.length) callbacks.assignItemLocations(unique);
    },
    relocateToDock: (items) => {
        const pallets = uniquePallets(items);
        if (pallets.length) {
            callbacks.storePallets(
                pallets.map((pallet) => ({ ...pallet, warehouse_location: DOCK_LOCATION, is_pool: true })),
                // Shares storePallets' write, but this moves an already-stored pallet to the dock —
                // a relocation, not a put-away.
                { eventType: 'ITEM_RELOCATED_IN_WAREHOUSE' }
            );
        }
    },
    flagException: (items) => {
        const unique = uniqueItems(items);
        if (unique.length) callbacks.editException({ type: 'PICKUP', items: unique, order: unique[0]?.order });
    },
    // Put every selected item — across orders — onto one new consolidated pallet.
    consolidate: (items) => {
        const unique = consolidatableItems(uniqueItems(items));
        if (unique.length > 1) callbacks.consolidateItems(unique, manifest);
    },
    // No location filter — staging clears warehouse_location rather than reading it. No confirm
    // either: high-volume floor work, reversible via Un-stage.
    pickStage: (items) => {
        const pallets = uniquePallets(items);
        if (pallets.length) callbacks.stagePallets(pallets);
    },
    // Open the dialog only — it resolves what else shares these pallets before anything is written.
    unreceive: (order) => {
        const pallets = orderReceivedPallets(order);
        if (pallets.length) callbacks.openUnwind({ mode: 'UNRECEIVE', order, pallets });
    },
    unstage: (order) => {
        const pallets = orderStagedPallets(order);
        if (pallets.length) callbacks.openUnwind({ mode: 'UNSTAGE', order, pallets });
    },
    // Rename a single item's pallet via the existing RenamePallet modal.
    renamePallet: (item) => {
        if (item?.pallet) callbacks.renamePalletInit(item.pallet);
    },
    // Open the staging-zone dialog for the whole manifest.
    setStagingZone: () => callbacks.setStagingZoneManifest(manifest),
    // Open the pallet location-history dialog for a pallet.
    palletLog: (pallet) => {
        if (pallet?.pallet_id) callbacks.openPalletLog(pallet);
    },
    // Open the partial-receive dialog for an item
    partialReceive: ({ item, order }) => {
        if (item) callbacks.openPartialReceive({ item, order });
    },
    // Open the resolution dialog for an item's open exception
    resolveException: ({ order, item }) => {
        const mapping = (item?.exceptions || []).find((m) => m?.exception && m.exception.status !== 'RESOLVED');
        const exception = mapping?.exception || item?.item_exception;
        if (!exception) return;
        const siblings = (order?.itemsByOrderId || []).filter(
            (i) => i?.item_exception?.exception_id === exception.exception_id
        );
        callbacks.setResolveExceptionParams({
            exception,
            items: siblings.length ? siblings : [item],
            order,
        });
    },
    // Printing works off the manifest narrowed to the selected items (labels are built from
    // orders[].manifestItems). Item labels and pallet labels are separate actions.
    printItemLabels: (items) => {
        const { printable, skipped } = partitionPlaceholders(manifest, uniqueItems(items));
        if (!printable.length) {
            callbacks.setNotification({ severity: 'warning', message: PLACEHOLDER_PRINT_BLOCKED });
            return;
        }
        if (skipped) {
            callbacks.setNotification({ severity: 'warning', message: placeholdersSkippedMessage(skipped) });
        }

        const narrowed = narrowManifestToItems(manifest, printable);
        if (narrowed) callbacks.printItemLabels(narrowed);
    },
    printPalletLabels: (items) => {
        const narrowed = narrowManifestToItems(manifest, items);
        if (narrowed) callbacks.printPalletLabels(narrowed);
    },
    // WILL_CALL: pick up all pallets, then complete the fully-staged orders
    completePickup: (orders = []) => {
        const pallets = collectManifestPalletsFromOrders(orders);
        return callbacks
            .pickupPallets(pallets)
            .then(() =>
                callbacks.completeOrders(
                    orders.filter((order) =>
                        (order.manifestItems || []).every((item) =>
                            STAGED_STATUSES.includes(item?.pallet?.warehouse_status)
                        )
                    )
                )
            );
    },
});
